import { z } from 'zod';
import { Cuid2 } from './primitives.js';
/**
 * Public images (D33, ADR 0006).
 *
 * Cloudinary serves them; we store what it takes to build a URL, never a URL.
 * A stored URL welds the host, the account and the transformation defaults
 * into every row, so changing any of them later means a migration. With the
 * id and the dimensions, one helper in `@myhome/ui` builds every variant and
 * swapping host is a change to that helper.
 *
 * Private files (provider documents, the member card photo) are not Media.
 * They stay in R2 behind short-lived signed URLs and never reach Cloudinary.
 */
/** Where a photo sits in moderation. Only APPROVED is ever shown publicly. */
export const MEDIA_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
/**
 * Cloudinary's identifier for an asset, e.g. `providers/abc123/work-1`.
 *
 * Folder segments and the name, no extension and no version: the helper adds
 * the transformation and the format. Rejecting a full URL here is deliberate,
 * so a URL cannot be stored by mistake.
 */
export const CloudinaryPublicId = z
    .string()
    .trim()
    .min(1, 'A publicId is required.')
    .max(255, 'Keep the publicId under 255 characters.')
    .regex(/^[A-Za-z0-9][A-Za-z0-9_\-/]*$/, "A publicId is Cloudinary's path, like providers/abc/work-1. Not a URL, and no file extension.");
/**
 * Alt text. Required, because these images carry the meaning of the screen:
 * a feed of work photos read by a screen reader with no alt text is an empty
 * page. Empty string is allowed only for an image that is purely decorative,
 * which in practice nothing in the feed is.
 */
export const AltText = z.string().trim().max(300, 'Keep alt text under 300 characters.');
/** A pixel dimension Cloudinary reports back on upload. */
const Dimension = z.number().int().positive().max(20_000);
export const Media = z.object({
    id: Cuid2,
    publicId: CloudinaryPublicId,
    width: Dimension,
    height: Dimension,
    alt: AltText,
    /**
     * A tiny inline placeholder shown while the real image loads, so the feed
     * does not flash grey blocks on a slow connection. Null when it has not
     * been generated.
     */
    blurDataUrl: z.string().startsWith('data:image/').max(4096).nullable(),
    status: z.enum(MEDIA_STATUSES),
});
/**
 * Media as the public sees it. The status is gone, because everything that
 * reaches a member is APPROVED and saying so invites a screen to branch on
 * it.
 */
export const PublicMedia = Media.omit({ status: true });
/** At least this many approved work photos before a provider may submit (D32). */
export const MIN_PROVIDER_WORK_PHOTOS = 3;
/** Phones shoot HEIC; browsers do not display it, so Cloudinary converts on the way in. */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
/** 10 MB. A modern phone photo is 3 to 5 MB, so this takes one without taking a video. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
//# sourceMappingURL=media.js.map